import {
  computed,
  inject,
  provide,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter
} from 'vue'

/**
 * One reactive value announced down the tree, with an answer for the components rendered
 * outside any announcer.
 *
 * The pair it returns is what a module here republishes under its own names — see
 * `listErrorState`, `listTotalState`, `loadingState`. Announce through one of those rather
 * than calling this at a slice: the fallback is the whole point, and getting it wrong is
 * silent — `inject` without one returns `undefined`, which a consumer cannot tell from a
 * real announcement.
 *
 * The fallback is a **value**, wrapped in a fresh `computed` on every read, so a consumer
 * that got the default instead of a provider cannot write through it to the next one.
 */
export function createInjectedState<T>(name: string, fallback: T) {
  const key: InjectionKey<ComputedRef<T>> = Symbol(name)

  const provideState = (source: MaybeRefOrGetter<T>) => {
    provide(
      key,
      computed(() => toValue(source))
    )
  }

  const useState = (): ComputedRef<T> =>
    inject(
      key,
      computed(() => fallback)
    )

  return { provideState, useState }
}
